import moment from 'moment';
import React, { FC } from 'react';
import styled from 'styled-components';

import { SessionUserI } from '~src/interfaces/session.types';
import { SurveyExtended } from '~src/modules/surveys/interfaces';
import { getFormattedSubjectAndUseCaseTranslationKey } from '~src/modules/surveys/services/survey';
import { routes } from '~src/routingConfig';
import { translateContent } from '~src/services/translationServices';
import { Uuid } from '~src/services/uuid';
import StatusBox from '~src/shared/StatusBox';
import ListItem from '~src/shared/lists/listItem/ListItem';
import { HoverContent, ListItemProps } from '~src/shared/lists/listItem/types';
import { red } from '~src/styles/colors';
import { responseRateFormat } from '~src/utils';
import {useTranslation} from "react-i18next";
import {SurveyStatus} from "~src/interfaces/surveys.interfaces";

const RightSection = styled.div`
  flex: 0 1 225px;
  border-left: 1px solid ${({ theme }) => theme.black.a1};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 15px;
`;
const RatePercentage = styled.div`
  font-size: 1.4rem;
  color: ${({ theme }) => theme.baseBlue.a10};
`;
const RateText = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.baseBlue.a6};
`;

interface Props {
  survey: SurveyExtended;
  duplicateSurvey: (surveyId: string) => void;
  deleteSurvey: (surveyId: string) => void;
  itemType?: 'survey' | 'userSurvey';
  sessionUser?: SessionUserI;
}

const statusBoxStyles = {
  marginLeft: 10,
  padding: '4px 11px',
};

const listDomain = 'x.surveys.list.';
const infoKeyDomain = `${listDomain}details.item.info.`;

const SurveyItem: FC<Props> = React.memo(
  function SurveyItem({ itemType = 'survey', survey, duplicateSurvey, deleteSurvey, sessionUser }: Props) {
    const { t } = useTranslation();
    const itemProps: ListItemProps<string> = [
      {
        key: `${infoKeyDomain}questions.amount`,
        txt: t(`${infoKeyDomain}questions.amount`, { count: survey.number_of_questions }),
      },
      {
        key: `${infoKeyDomain}answers.amount`,
        txt: t(`${infoKeyDomain}answers.amount`, { count: survey.number_of_answers }),
      },
    ];
    if (survey.last_answered_at) {
      itemProps.push({
        key: `${infoKeyDomain}latest_answer_date`,
        txt: t(`${infoKeyDomain}latest_answer_date`, {
          date: moment(survey.last_answered_at).format('DD MMM YYYY'),
        }),
      });
    }
    switch (itemType) {
      case 'userSurvey':
        itemProps.unshift({
          key: `${infoKeyDomain}about`,
          txt: t(`${infoKeyDomain}about`, { name: survey.about && survey.about[0].meta.name }),
        });
        break;

      default:
        itemProps.unshift(
          {
            key: getFormattedSubjectAndUseCaseTranslationKey({useCase: survey.use_case, subject: survey.subject}),
            txt: t(getFormattedSubjectAndUseCaseTranslationKey({useCase: survey.use_case, subject: survey.subject})),
          },
          {
            key: `${infoKeyDomain}topics.amount`,
            txt: t(`${infoKeyDomain}topics.amount`, { count: survey.number_of_topics }),
          },
        );
        break;
    }
    const duplicateThisSurvey = () => {
      duplicateSurvey(survey.id);
    };

    const deleteThisSurvey = () => {
      deleteSurvey(survey.id);
    };

    const surveyType = itemType === 'userSurvey' ? '.user' : '';

    const hoverContent: HoverContent = {
      wideBtn: {
        key: `${listDomain}item.goto_report_text`,
        hoverTxt: t(`${listDomain}item.goto_report_text`),
        link: routes.reports.survey(Uuid.fromHexString(survey.id)),
        icon: 'iconReports',
      },
    };
    if (!sessionUser || !survey.created_by || survey.created_by === sessionUser.uuid) {
      hoverContent.smallBtns = [
        {
          key: `${listDomain}details.item.edit_label`,
          hoverTxt: t(`${listDomain}details.item.edit_label`),
          link: `/content/surveys/${survey.id}/?collapsedTitleForm`,
        },
        {
          key: `${listDomain}details.item.copy_label${surveyType}`,
          hoverTxt: t(`${listDomain}details.item.copy_label${surveyType}`),
          click: duplicateThisSurvey,
          icon: 'iconPlusBox',
        },
        {
          key: `${listDomain}details.item.delete_label${surveyType}`,
          hoverTxt: t(`${listDomain}details.item.delete_label${surveyType}`),
          click: deleteThisSurvey,
          icon: 'iconBin',
          hoverColor: red.a10,
        },
      ];
    } else {
      hoverContent.wideBtn!.hoverTxt = t(`${listDomain}item.goto_report_text.single`);
      hoverContent.wideBtn!.key = `${listDomain}item.goto_report_text.single`;
    }

    const rightSection = (
      <RightSection>
        <RatePercentage>{`${responseRateFormat(survey.response_rate)}%`}</RatePercentage>
        <RateText data-translation={`${listDomain}details.item.response_rate`}>
          {t(`${listDomain}details.item.response_rate`)}
        </RateText>
      </RightSection>
    );

    return (
      <ListItem
        title={translateContent(survey.name)}
        itemProps={itemProps}
        hoverContent={hoverContent}
        titleContent={<StatusBox active={survey.status === SurveyStatus.Active} style={statusBoxStyles} />}
        rightSection={rightSection}
      />
    );
  },
);

export default SurveyItem;
